import React, {useState} from 'react';
import {IconButton, MenuItem, TableCell, TableRow} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from '@material-ui/icons/Edit';
import {MetaData} from "@src/Interfaces/Entity/Location";
import {ISelectedItem} from "@src/Components/Pages/Main/Content/Locations/LocationDialog/MetaDataTab/MetaDataTab";
import {FullWidthTextField} from "@src/Shared/styles";
import {IError} from "@src/Components/Pages/Main/Content/Locations/LocationDialog/LocationDialog";
import {Values} from "Tools/Values";


interface IMetaDataTabItem {
    metaData: MetaData;
    metaDataTypes: ISelectedItem[];
    error?: IError;
    canEdit: boolean;
    canEditDropdownOptions: boolean;
    canChangeValues: boolean;
    onRemove: () => void;
    onChange: (data: MetaData) => void;
    onTypeChange: (type: number) => void;
}

export const MetaDataTabItem = (props: IMetaDataTabItem) => {
    const [showEdit, setShowEdit] = useState(false);

    const onNameChange = (name: string) => {
        props.onChange({...props.metaData, name});
    }

    const onValueChange = (value: string) => {
        props.onChange({...props.metaData, value});
    }

    const onEditDropdownOptionsChange = (value: string) => {
        const dropdownTypeOptions = value.split(";");
        props.onChange({...props.metaData, dropdownTypeOptions });
    }

    const renderDefinedInput = (type: string) => (
        <FullWidthTextField
            disabled={!props.canChangeValues}
            type={type}
            value={props.metaData.value}
            placeholder={Values.getLocalizationValue("value")}
            onChange={(ev: any) => onValueChange(ev.target.value)}
            variant="outlined"
            size={"small"}
        />
    )

    const renderTextOrNumberMetaDataValue = (type: string = "text") => (
        <FullWidthTextField
            disabled={!props.canChangeValues}
            id="outlined-number"
            type={type}
            placeholder={Values.getLocalizationValue("value")}
            value={props.metaData.value}
            onChange={(ev: any) => onValueChange(ev.target.value)}
            variant="outlined"
            size={"small"}
        />
    )

    const renderDropdownOptions = () => (
        <FullWidthTextField
            select
            disabled={!props.canEditDropdownOptions}
            error={props.error?.value}
            label={props.error?.value}
            value={props.metaData.value}
            onChange={(ev: any) => onValueChange(ev.target.value)}
            variant="outlined"
            size={"small"}
        >
            {props.metaData.dropdownTypeOptions?.map((option: string, i: number) => (
                <MenuItem key={i} value={option}>
                    {option}
                </MenuItem>)
            )}
        </FullWidthTextField>
    )

    const renderEditDropdownOptions = () => (
        <FullWidthTextField
            disabled={!props.canEditDropdownOptions}
            value={props.metaData?.dropdownTypeOptions.join(";")}
            placeholder={Values.getLocalizationValue("values")}
            variant={"outlined"}
            size={"small"}
            onChange={(ev: any) => onEditDropdownOptionsChange(ev.target.value)}
        />
    )

    const renderDropdown = () => (
        <>
            <div style={{display: "flex", alignItems: "center"}}>
                {showEdit
                    ? renderEditDropdownOptions()
                    : renderDropdownOptions()
                }

                {props.canEdit &&
                    <IconButton
                        style={{marginLeft: "8px", padding: "8px"}}
                        onClick={() => setShowEdit(!showEdit)}
                    >
                        <EditIcon/>
                    </IconButton>
                }
            </div>
            {showEdit &&
                <span style={{fontSize: "12px", paddingTop: "4px", color: "rgba(0, 0, 0, 0.54)"}}>
                    {Values.getLocalizationValue("useAsSeparator")}
                </span>
            }
        </>
    )

    const renderDateMetaDataValue = () => (
        <FullWidthTextField
            disabled={!props.canChangeValues}
            id="date"
            type="date"
            value={props.metaData.value}
            variant={"outlined"}
            size={"small"}
            onChange={(ev: any) => onValueChange(ev.target.value)}
        />
    )

    const renderDateAndTimeMetaDataValue = () => (
        <FullWidthTextField
            disabled={!props.canChangeValues}
            id="datetime-local"
            type="datetime-local"
            value={props.metaData.value}
            variant={"outlined"}
            size={"small"}
            onChange={(ev: any) => onValueChange(ev.target.value)}
        />
    )

    const renderMetaDataValue = () => {
        if (!props.canChangeValues && !props.metaData.value) {
            return renderTextOrNumberMetaDataValue();
        }
        
        switch (props.metaData.type) {
            case 0:
                return renderDefinedInput("defined");
            case 1:
                return renderTextOrNumberMetaDataValue();
            case 2:
                return renderTextOrNumberMetaDataValue("number");
            case 3:
                return renderDropdown();
            case 4:
                return renderDateMetaDataValue();
            case 5:
                return renderDateAndTimeMetaDataValue();
        }
    }

    return (
        <TableRow>
            <TableCell vertical-align="top" style={{verticalAlign: 'top'}}>
                <FullWidthTextField
                    disabled={!props.canEdit}
                    error={props.error?.name}
                    label={props.error?.name}
                    value={props.metaData.name}
                    onChange={(ev: any) => onNameChange(ev.target.value)}
                    placeholder={Values.getLocalizationValue("name")}
                    variant="outlined"
                    size={"small"}
                />
            </TableCell>

            <TableCell vertical-align="top" style={{verticalAlign: 'top'}}>
                <FullWidthTextField
                    select
                    disabled={!props.canEdit}
                    value={props.metaData.type}
                    onChange={(ev: any) => props.onTypeChange(ev.target.value)}
                    placeholder={Values.getLocalizationValue("type")}
                    variant="outlined"
                    size={"small"}
                >
                    {props.metaDataTypes?.map((type: ISelectedItem, i: number) => (
                        <MenuItem key={i} value={type.typeValue}>
                            {type.typeName}
                        </MenuItem>)
                    )}
                </FullWidthTextField>
            </TableCell>

            <TableCell style={{paddingBottom: `${showEdit ? 6 : 8}px`, verticalAlign: 'top'}}>
                {renderMetaDataValue()}
            </TableCell>

            <TableCell size={"small"} align={"center"} style={{verticalAlign: 'top'}}>
                <IconButton
                    onClick={() => props.onRemove()}
                    disabled={!props.canEdit}
                >
                    <DeleteIcon/>
                </IconButton>
            </TableCell>
        </TableRow>
    );
}
